import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Wrench } from "lucide-react";
import { api } from "@/lib/api";

// Paths that remain reachable even when maintenance is ON, so admins can flip
// the switch back off and users can finish reading legal pages.
const ALLOWED_DURING_MAINTENANCE = [
  "/login",
  "/auth/verify",
  "/admin",
  "/privacy",
  "/terms",
  "/guidelines",
];

const isAllowed = (pathname) =>
  ALLOWED_DURING_MAINTENANCE.some((p) => pathname === p || pathname.startsWith(p + "/"));

export default function MaintenanceGate({ children }) {
  const [state, setState] = useState({ loaded: false, enabled: false, message: "" });
  const location = useLocation();

  useEffect(() => {
    let alive = true;
    api
      .get("/site-settings")
      .then((r) => {
        if (!alive) return;
        const m = r.data?.maintenance || { enabled: false, message: "" };
        setState({ loaded: true, enabled: Boolean(m.enabled), message: m.message || "" });
      })
      .catch(() => {
        if (alive) setState({ loaded: true, enabled: false, message: "" });
      });
    return () => {
      alive = false;
    };
  }, [location.pathname]);

  // Block painting public pages until we know maintenance state — avoids a flash.
  if (!state.loaded && !isAllowed(location.pathname)) {
    return <div className="min-h-screen bg-transparent" data-testid="maintenance-loading" />;
  }

  if (state.enabled && !isAllowed(location.pathname)) {
    return <MaintenancePage message={state.message} />;
  }

  return children;
}

function MaintenancePage({ message }) {
  return (
    <div className="min-h-screen bg-transparent text-white" data-testid="maintenance-page">
      <div className="mx-auto flex min-h-screen max-w-2xl flex-col items-center justify-center px-6 py-20 text-center">
        <div className="inline-flex h-20 w-20 items-center justify-center rounded-full border border-[#A855F7]/40 bg-[#A855F7]/10">
          <Wrench className="h-9 w-9 text-[#A855F7]" />
        </div>
        <p className="mt-8 text-[10px] font-bold uppercase tracking-[0.28em] text-[#A855F7]">
          Scheduled maintenance
        </p>
        <h1 className="mt-3 font-display text-4xl font-black leading-[1.05] tracking-tight sm:text-5xl">
          We&apos;re upgrading the Ambassador platform.
        </h1>
        <p className="mt-5 max-w-lg text-zinc-400">
          {message ||
            "We're tuning things up for the next cohort. The site will be back online shortly — your application, points and submissions are safe."}
        </p>
        <div className="mt-10 flex flex-wrap items-center justify-center gap-3 text-sm">
          <a
            href="mailto:ambassadors@noobsync.com"
            className="rounded-md border border-zinc-800 bg-zinc-950 px-5 py-2.5 font-semibold text-zinc-200 hover:border-[#A855F7]/40"
            data-testid="maintenance-contact"
          >
            ambassadors@noobsync.com
          </a>
          <a
            href="https://noobsync.com"
            className="rounded-md border border-transparent px-5 py-2.5 font-semibold text-zinc-400 hover:text-white"
            data-testid="maintenance-noobsync"
          >
            ← Back to noobsync.com
          </a>
        </div>
        <p className="mt-12 text-[10px] uppercase tracking-[0.28em] text-zinc-700">
          Thanks for your patience.
        </p>
      </div>
    </div>
  );
}
